/**
 * API Client
 * Axios-based HTTP client for API requests
 */

import axios from 'axios';
import logger from './logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Default request timeout (ms) — tuned for CRUD calls so genuine failures surface quickly.
const DEFAULT_TIMEOUT = 30000;

// Reports legitimately take longer than CRUD calls: they pull large result sets
// (limit=500–1000) and the first request after a (re)login can hit a cold tenant
// pool on Azure. Use this per-call override (config.timeout) on report endpoints
// instead of raising DEFAULT_TIMEOUT globally — see ReportsPage. A request that
// exceeds the timeout aborts client-side (ECONNABORTED) even though the API may
// still complete and log a 200, which reads to the user as a "connection error".
export const REPORT_TIMEOUT = 90000;

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for Azure Easy Auth cookies
});

/**
 * Fire a fire-and-forget session-event beacon to the API just before a client-forced
 * logout/redirect. Deliberately does NOT use the apiClient instance (avoids re-entering this
 * interceptor) and uses navigator.sendBeacon / fetch keepalive so the request survives the
 * imminent page navigation. Never throws.
 */
const sendSessionEventBeacon = (reason, originalUrl, status) => {
  try {
    let email;
    try {
      const externalUser = sessionStorage.getItem('externalUser');
      if (externalUser) email = JSON.parse(externalUser).email;
    } catch (e) { /* no/invalid external user data */ }

    const url = `${API_URL}/api/auth/session-event`;
    const payload = JSON.stringify({ reason, path: originalUrl, status, email });

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => { /* diagnostics only */ });
    }
  } catch (e) {
    // Diagnostics only — never block the logout on a beacon failure.
  }
};

// ---------------------------------------------------------------------------
// Rate-limit (429) handling
// ---------------------------------------------------------------------------

// Module-scoped "currently backing off" flag. Set by the first 429 in a burst and
// cleared after the delay, so a 14-call fan-out that all 429 doesn't fire 14 retries
// into an already-exhausted bucket (which would only deepen the overage).
let rateLimitBackingOff = false;

/**
 * Notify the app that we've been throttled. The interceptor lives outside React, so
 * we dispatch a window CustomEvent that a small listener (mounted under
 * NotificationProvider) turns into a toast. Because the very first 429 can fire during
 * app bootstrap BEFORE that listener mounts, we also buffer the latest event on
 * `window.__lastRateLimitEvent` so the listener can replay it on mount. Debounced so a
 * fan-out of 429s shows one toast, not many.
 */
let lastRateLimitToastAt = 0;
const notifyRateLimited = (retryAfterSeconds) => {
  const now = Date.now();
  const detail = { retryAfterSeconds, at: now };
  try { window.__lastRateLimitEvent = detail; } catch (e) { /* no window */ }
  // Debounce: at most one toast per 3s regardless of how many calls 429'd.
  if (now - lastRateLimitToastAt < 3000) return;
  lastRateLimitToastAt = now;
  try { window.dispatchEvent(new CustomEvent('api:rate-limited', { detail })); } catch (e) { /* no window */ }
};

/**
 * Parse Retry-After (seconds, or an HTTP-date) into milliseconds, or null if absent.
 */
const parseRetryAfterMs = (error) => {
  const h = error.response?.headers?.['retry-after'];
  if (!h) return null;
  const asInt = parseInt(h, 10);
  if (Number.isFinite(asInt) && String(asInt) === String(h).trim()) return asInt * 1000;
  const asDate = Date.parse(h);
  if (Number.isFinite(asDate)) return Math.max(0, asDate - Date.now());
  return null;
};

/**
 * Handle a 429. Always shows the throttle toast. For idempotent GETs (not already
 * retried, not in an active backoff burst), waits a short bounded delay and retries
 * ONCE. Everything else — POST/PUT/DELETE, an already-retried GET, or a request during
 * an active burst — is rejected so callers see the 429 (no retry storm).
 */
const handleRateLimited = (error) => {
  const retryAfterMs = parseRetryAfterMs(error);
  notifyRateLimited(retryAfterMs ? Math.ceil(retryAfterMs / 1000) : null);

  const config = error.config || {};
  const isGet = (config.method || '').toLowerCase() === 'get';
  const alreadyRetried = config.__retried === true;

  // Only retry idempotent GETs, once, and only when not already backing off from a burst,
  // and only when the wait is short (a small Retry-After, or our own short backoff).
  const CAP_MS = 5000;
  const wait = retryAfterMs != null ? Math.min(retryAfterMs, CAP_MS) : 1000 + Math.floor(Math.random() * 1000);
  if (isGet && !alreadyRetried && !rateLimitBackingOff && wait <= CAP_MS) {
    rateLimitBackingOff = true;
    // Mutate the flag IN PLACE on the same config object and re-issue that same object,
    // so the retried request carries __retried and can't retry again (a spread/copy would
    // drop the flag and loop).
    config.__retried = true;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        rateLimitBackingOff = false;
        apiClient(config).then(resolve).catch(reject);
      }, wait);
    });
  }

  return Promise.reject(error);
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Let browser set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Check for tenant in URL query parameter (takes precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');

    // Add X-Tenant-ID header from URL parameter or localStorage
    if (tenantParam) {
      // Use tenant code from URL
      config.headers['X-Tenant-ID'] = tenantParam;
    } else {
      // Fall back to manually selected tenant from localStorage
      const selectedTenantId = localStorage.getItem('selectedTenantId');
      if (selectedTenantId) {
        config.headers['X-Tenant-ID'] = selectedTenantId;
      }
    }

    // For external users, attach Keycloak Bearer token
    const keycloakToken = sessionStorage.getItem('keycloakToken');
    if (keycloakToken) {
      config.headers['Authorization'] = `Bearer ${keycloakToken}`;
    }

    // In dev mode, send the logged-in user's email to the backend
    // This allows the dev auth middleware to authenticate as the correct user
    // BUT ONLY if not using Keycloak (external user authentication)
    const isDev = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
    if (isDev && !keycloakToken) {
      const devUserEmail = sessionStorage.getItem('devUserEmail') || process.env.REACT_APP_DEV_USER_EMAIL;
      if (devUserEmail) {
        config.headers['x-ms-client-principal-name'] = devUserEmail;
      }
    }

    // For internal users in production, attach Azure AD access token
    const azureAccessToken = sessionStorage.getItem('azureAccessToken');
    if (azureAccessToken && !keycloakToken) {
      config.headers['Authorization'] = `Bearer ${azureAccessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Remember the server correlation id of the most recent call so the ErrorBoundary and
    // error logs can quote it for support / log correlation.
    const reqId = response.headers?.['x-request-id'];
    if (reqId) {
      try { sessionStorage.setItem('lastRequestId', reqId); } catch (e) { /* sessionStorage unavailable */ }
    }

    // Check if backend auto-switched tenant (user lost access to stored tenant)
    const switchedTenantId = response.headers['x-tenant-switched'];
    if (switchedTenantId) {
      localStorage.setItem('selectedTenantId', switchedTenantId);
      // Reload to apply new tenant context — but guard against a reload LOOP. If the
      // backend keeps returning x-tenant-switched (a header/state disagreement it won't
      // resolve), an unconditional reload would spin: reload -> bootstrap fan-out ->
      // header again -> reload. Suppress a repeat reload within 5s (same pattern as the
      // 401 redirect guard below) and log it so the disagreement is diagnosable.
      const lastSwitchReload = sessionStorage.getItem('lastTenantSwitchReload');
      const now = Date.now();
      if (lastSwitchReload && (now - parseInt(lastSwitchReload, 10)) < 5000) {
        logger.warn('Tenant-switch reload loop detected, suppressing reload', { event: 'tenant_switch_reload_loop', switchedTenantId });
        return response;
      }
      try { sessionStorage.setItem('lastTenantSwitchReload', now.toString()); } catch (e) { /* sessionStorage unavailable */ }
      window.location.reload();
      return response;
    }

    // Self-heal when the backend resolved a tenant for a request that did not
    // carry an explicit X-Tenant-ID (the API only stamps X-Tenant-Resolved on
    // non-header resolution — see tenantResolver). We adopt the resolved id into
    // localStorage so every SUBSEQUENT request is explicitly scoped to it, and
    // the server keeps enforcing isolation (an unauthorized X-Tenant-ID yields a
    // 403, not a silent switch). We deliberately do NOT call
    // window.location.reload() here: a hard reload mid-workflow tears down any
    // open modal/wizard/unsaved form (the original cause of the upload-resets-modal
    // bug, and a prior reload-loop documented in Navbar.js). TenantContext remains
    // the single source of truth for the displayed tenant and reconciles on its own.
    const resolvedTenantId = response.headers['x-tenant-resolved'];
    if (resolvedTenantId) {
      const storedTenantId = localStorage.getItem('selectedTenantId');
      if (!storedTenantId) {
        // First-ever request — adopt the resolved tenant silently
        localStorage.setItem('selectedTenantId', resolvedTenantId);
      } else if (storedTenantId !== resolvedTenantId) {
        logger.warn('Tenant mismatch corrected (next request explicitly scoped, no reload)', { event: 'tenant_mismatch' });
        localStorage.setItem('selectedTenantId', resolvedTenantId);
      }
    }

    return response;
  },
  (error) => {
    const reqId = error.response?.headers?.['x-request-id'];
    if (reqId) {
      try { sessionStorage.setItem('lastRequestId', reqId); } catch (e) { /* sessionStorage unavailable */ }
    }
    // Log redacted metadata only — never error.response.data wholesale (may contain PII).
    logger.error('API error', {
      status: error.response?.status,
      url: error.config?.url,
      requestId: reqId,
      message: extractApiError(error),
    });

    // Rate limited (429). Handle BEFORE the 401 block so a throttle can never be
    // mistaken for an auth failure and trigger the forced /login redirect. Surfaces
    // a single throttle toast (via a window event → NotificationContext bridge) and,
    // for idempotent GETs only, does ONE bounded retry after a short backoff.
    if (error.response?.status === 429) {
      return handleRateLimited(error);
    }

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Note: API returns { success: false, error: { type, message } }
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || '';

      // Don't redirect on "User not found" - PermissionContext will handle this
      // by setting hasNoTenantAccess = true and routing to /no-access
      // This allows new users (authenticated via Azure AD but not in authorized_users)
      // to be shown the access request page instead of being stuck in a redirect loop
      if (errorMessage.includes('User not found') || errorMessage.includes('not found')) {
        return Promise.reject(error);
      }

      // Unauthorized - redirect to login (but not if already on login page)
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/';
      if (isOnLoginPage) {
        // Already on login page, don't redirect (would cause loop)
        return Promise.reject(error);
      }

      // Loop guard: don't redirect if we already redirected within the last 5 seconds
      const lastAuthRedirect = sessionStorage.getItem('lastAuthRedirect');
      const now = Date.now();
      if (lastAuthRedirect && (now - parseInt(lastAuthRedirect, 10)) < 5000) {
        logger.warn('Auth redirect loop detected, suppressing redirect', { event: 'auth_redirect_loop' });
        return Promise.reject(error);
      }
      sessionStorage.setItem('lastAuthRedirect', now.toString());

      // Make this client-forced logout visible: a 401 on any request silently redirects the user
      // to /login, which reads to them as being "logged out for no reason". Log it and beacon the
      // server (before we navigate away) with the offending endpoint so it is diagnosable from logs.
      logger.warn('Forced logout on 401', { event: 'forced_logout_401', originalUrl: error.config?.url, status: 401 });
      sendSessionEventBeacon('forced_401', error.config?.url, 401);

      const isExternalUser = sessionStorage.getItem('userType') === 'external_client';
      if (isExternalUser) {
        // Clear Keycloak session and redirect to login page
        sessionStorage.removeItem('keycloakToken');
        sessionStorage.removeItem('keycloakRefreshToken');
        sessionStorage.removeItem('userType');
        sessionStorage.removeItem('externalUser');
        window.location.href = '/login';
      } else {
        // Redirect to login page - user clicks "Sign in with Microsoft"
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      // `error.response.data.error` is EITHER a string code (strict tenantResolver:
      // 'tenant_access_denied' / 'no_tenant_access') OR an object
      // ({ type: 'ForbiddenError', message: '...requested tenant' }) from
      // optionalTenantResolver's inaccessible-X-Tenant-ID branch. Normalise both so
      // the stale-selected-tenant case is recognised — otherwise selectedTenantId is
      // never cleared and the app loops on a tenant the user no longer has access to.
      const rawError = error.response?.data?.error;
      const errorCode = typeof rawError === 'object' ? rawError?.type : rawError;
      const errorMessage = (typeof rawError === 'object' ? rawError?.message : error.response?.data?.message) || '';
      const isStaleTenant =
        errorCode === 'tenant_access_denied' ||
        errorCode === 'no_tenant_access' ||
        (errorCode === 'ForbiddenError' && /requested tenant/i.test(errorMessage));

      if (isStaleTenant) {
        // Clear the stale tenant selection so the next load re-resolves to a tenant the
        // user actually has (or a clean no-access), instead of re-sending the bad
        // X-Tenant-ID header forever. Do NOT redirect here (avoids redirect loops);
        // PermissionContext catches the error and routes to /no-access if truly none.
        logger.warn('Tenant access denied - clearing stale selectedTenantId', { event: 'tenant_access_denied' });
        localStorage.removeItem('selectedTenantId');
      }
      // Other 403s already logged above as 'API error'.
    }

    return Promise.reject(error);
  }
);

/**
 * GET request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiGet = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * POST request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPost = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPut = async (url, data, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiDelete = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload file (multipart/form-data)
 * @param {string} url - API endpoint
 * @param {FormData} formData - Form data with file
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiUpload = async (url, formData, config = {}) => {
  try {
    // Do NOT set Content-Type here. The request interceptor strips it for
    // FormData so the browser can set `multipart/form-data; boundary=...`
    // itself — a hard-coded value would omit the boundary and break parsing.
    const response = await apiClient.post(url, formData, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download a binary file via POST (e.g. xlsx export)
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Blob>} Response blob
 */
export const apiDownload = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, { ...config, responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Extract data from API response wrapper.
 * Handles the various response formats returned by the backend:
 *   - { success: true, data: [...] }  (standard list response)
 *   - { success: true, data: { ... } } (standard object response)
 *   - [...] (direct array)
 *   - { data: [...] } (axios-wrapped)
 *
 * @param {*} response - API response (already unwrapped by apiGet/apiPost)
 * @param {*} defaultValue - Default value if extraction fails (default: [])
 * @returns {*} The extracted data
 */
export const extractApiData = (response, defaultValue = []) => {
  if (!response) return defaultValue;
  if (response?.success && response?.data !== undefined) return response.data;
  if (response?.data !== undefined) return response.data;
  if (Array.isArray(response)) return response;
  return defaultValue;
};

/**
 * Extract error message from API error response.
 * Handles the various error formats:
 *   - err.response.data.error.message (standard API error)
 *   - err.response.data.message (simple error)
 *   - err.message (network/other error)
 *
 * @param {Error} err - Error object from catch block
 * @param {string} fallback - Fallback message
 * @returns {string} The error message
 */
export const extractApiError = (err, fallback = 'An unexpected error occurred') => {
  return err?.response?.data?.error?.message
    || err?.response?.data?.message
    || err?.message
    || fallback;
};

/**
 * Produce a user-facing message that distinguishes the *kind* of failure rather
 * than masking everything as a single generic string. The three cases a caught
 * Axios error can fall into:
 *   - timeout  : err.code === 'ECONNABORTED' (request aborted client-side after
 *                config.timeout) — the server may still be processing.
 *   - network  : no err.response at all (CORS, DNS, offline, connection refused).
 *   - server   : err.response present — surface the API's own error message.
 *
 * @param {Error} err - Error object from a catch block
 * @param {Object} [opts]
 * @param {string} [opts.action] - Short verb phrase for context, e.g. "load the report".
 * @returns {string} A specific, user-facing message.
 */
export const describeApiError = (err, { action = 'complete the request' } = {}) => {
  if (err?.code === 'ECONNABORTED') {
    return `The request took too long and timed out. The server may still be working — try a narrower date range, or retry.`;
  }
  if (!err?.response) {
    return `Lost connection to the server while trying to ${action}. Check your network and retry.`;
  }
  return extractApiError(err, `Failed to ${action}. Please try again.`);
};

/**
 * Clear selected tenant (call on logout)
 */
export const clearSelectedTenant = () => {
  localStorage.removeItem('selectedTenantId');
};

/**
 * Get currently selected tenant ID
 */
export const getSelectedTenantId = () => {
  return localStorage.getItem('selectedTenantId');
};

/**
 * Set selected tenant ID
 */
export const setSelectedTenantId = (tenantId) => {
  if (tenantId) {
    localStorage.setItem('selectedTenantId', tenantId);
  } else {
    localStorage.removeItem('selectedTenantId');
  }
};

export default apiClient;
